// V12.x — Email admin centralisé, chargé dynamiquement depuis /api/site-settings
// puis mis en cache mémoire. Fallback statique : anna.yyc@gmail.com.
//
// Tous les mailto affichés dans l'UI passent par `adminMailto()` et pointent
// donc vers l'email configuré depuis /admin/site-settings (zone « Contact admin »).
import api from "@/lib/api";

/** Fallback statique — utilisé tant que le cache n'est pas peuplé */
export const ADMIN_EMAIL_FALLBACK = "anna.yyc@gmail.com";

let _cachedEmail = ADMIN_EMAIL_FALLBACK;
let _loadPromise = null;

/** Renvoie l'email admin courant (sync). Toujours défini (fallback). */
export function getAdminEmail() {
  return _cachedEmail || ADMIN_EMAIL_FALLBACK;
}

/** Précharge l'email admin depuis site-settings. Idempotent. */
export function preloadAdminEmail() {
  if (_loadPromise) return _loadPromise;
  _loadPromise = api
    .get("/site-settings")
    .then((r) => {
      const email = r?.data?.admin_contact?.email;
      if (email && typeof email === "string" && email.includes("@")) {
        _cachedEmail = email.trim();
      }
      return _cachedEmail;
    })
    .catch(() => _cachedEmail);
  return _loadPromise;
}

/** Force le rafraîchissement du cache (appelé après un patch admin). */
export function refreshAdminEmail() {
  _loadPromise = null;
  return preloadAdminEmail();
}

/** Compat V11.18 — export nommé équivalent à getAdminEmail(). */
// eslint-disable-next-line
export const ADMIN_EMAIL = new Proxy({}, {
  get() { return getAdminEmail(); },
  // Astuce : ADMIN_EMAIL est utilisé comme string via template literals, mais
  // on garde aussi un accès direct via .toString().
});
// Fallback simple pour usages string : la lib retourne l'email courant.
Object.defineProperty(ADMIN_EMAIL, Symbol.toPrimitive, {
  value: () => getAdminEmail(),
});

/**
 * Construit un `mailto:` complet avec sujet + corps encodés.
 * @param {{subject?: string, body?: string}} opts
 */
export function adminMailto({ subject, body } = {}) {
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${getAdminEmail()}${params.length ? "?" + params.join("&") : ""}`;
}
